import {Inject, Injectable} from '@angular/core';
import {Observable, throwError, timeout, catchError, retry} from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Attachment, ConflictingCases, EmailDetails, IntakeEviction, IntakeEvictionList, LandingPageVisit, ProcessIntakeEviction, Tag } from '@ee/common/models';
import {IntakeEvictionType} from '@ee/common/enums';
import {AUTH_SESSION_TIMEOUT_STATUS_TEXT, AuthService} from './auth.service';
import {createSseStream, SseStreamEvent} from './sse-stream.util';

// Bulk deletion interfaces
export interface BulkDeletionValidationRequest {
  caseIds: string[];
  organizationId: string;
  userId: string;
}

export interface BulkDeletionRequest {
  caseIds: string[];
  organizationId: string;
  userId: string;
  reason?: string;
}

export interface DeletionConstraint {
  caseId: string;
  caseNumber: string;
  constraintType: 'RELATED_DOCUMENTS' | 'PAYMENTS' | 'WORKFLOW_ACTIVE';
  description: string;
  canProceed: boolean;
}

export interface DeletionSummary {
  caseCount: number;
  dateRange: {
    earliest: Date;
    latest: Date;
  };
  associatedData: {
    documents: number;
    notes: number;
    attachments: number;
  };
  constraints: DeletionConstraint[];
  estimatedTimeMinutes: number;
}

export interface BulkDeletionValidationResponse {
  canProceed: boolean;
  summary: DeletionSummary;
}

export interface DeletionFailure {
  caseId: string;
  caseNumber?: string;
  reason: string;
  errorType: 'PERMISSION_DENIED' | 'DATA_INTEGRITY_VIOLATION' | 'CASE_NOT_FOUND' | 'NOT_BULK_IMPORTED' | 'SYSTEM_ERROR';
}

export interface BulkDeletionResponse {
  success: boolean;
  deletedCount: number;
  failedCount: number;
  deletedCaseIds: string[];
  failedCases: DeletionFailure[];
  auditTrailId: string;
}

// Bulk process interfaces
export type BulkProcessErrorType =
  'NEEDS_MANUAL_PROCESSING' | 'PERMISSION_DENIED' | 'NOT_FOUND' | 'WORKFLOW_INVALID' | 'SYSTEM_ERROR';

export interface BulkProcessRequest {
  caseIds: string[];
  /** Absent = process each case on its own default workflow (EE-479). */
  selectedWorkflowId?: string;
  /** Absent = keep each case's resolved attorney. */
  selectedAttorneyUserId?: string;
  selectedCountyId?: string;
  computeCounties?: boolean;
  autoSelectCourt?: boolean;
  organizationId: string;
  userId: string;
}

export interface ProcessSuccess {
  caseId: string;
  newCaseId?: string;
  caseNumber?: string;
}

export interface ProcessFailure {
  caseId: string;
  caseNumber?: string;
  reason: string;
  errorType: BulkProcessErrorType;
}

export interface BulkProcessResponse {
  success: boolean;
  processedCount: number;
  skippedCount: number;
  failedCount: number;
  processedCases: ProcessSuccess[];
  skippedCases: ProcessFailure[];
  failedCases: ProcessFailure[];
  auditTrailId: string;
}

// Bulk reject interfaces
export type BulkRejectErrorType = 'PERMISSION_DENIED' | 'NOT_FOUND' | 'SYSTEM_ERROR';

export interface BulkRejectRequest {
  caseIds: string[];
  /** Shared rejection reason, included in every consolidated per-client rejection email. */
  reason: string;
  organizationId: string;
  userId: string;
}

export interface RejectFailure {
  caseId: string;
  caseNumber?: string;
  reason: string;
  errorType: BulkRejectErrorType;
}

export interface BulkRejectResponse {
  success: boolean;
  rejectedCount: number;
  failedCount: number;
  rejectedCaseIds: string[];
  failedCases: RejectFailure[];
  auditTrailId: string;
}

@Injectable({ providedIn: 'root' })
export class IntakeEvictionService {
  public resourceUrl: string;

  constructor(private http: HttpClient, private authService: AuthService, @Inject('environment') private environment) {
    this.resourceUrl = this.environment.api_prefix + 'api/intake-evictions';
  }

  updatePageLoadVisits(landingPageVisit: LandingPageVisit): Observable<LandingPageVisit> {
    return this.http.put<LandingPageVisit>(`${this.environment.api_prefix}api/page-visits`, landingPageVisit);
  }

  loadIntakeEvictionList(): Observable<IntakeEvictionList[]> {
    return this.http.get<IntakeEvictionList[]>(`${this.environment.api_prefix}api/intake-evictions/list`);
  }

  saveIntakeEviction(type: IntakeEvictionType, formData: FormData): Observable<string | undefined> {
    let url = this.resourceUrl;

    if (type === IntakeEvictionType.INTAKE_FORM) {
      url += '/intake-form';
    }

    return this.http.post(url, formData, {responseType: 'text'});
  }

  bulkImportIntakeEviction(evictions: any) {
    return this.http.post(this.resourceUrl + '/bulk-import', evictions);
  }

  findIntakeEvictionConflicts(id: string, clientId: string): Observable<ConflictingCases> {
    return this.http.get<ConflictingCases>(`${this.resourceUrl}/${id}/client/${clientId}/conflicts`);
  }

  addLandingPageVisit(attorneyId: string): Observable<any> {
    return this.http.post<LandingPageVisit>(`${this.environment.api_prefix}api/page-visits/${attorneyId}`, null);
  }

  processIntakeEviction(id: string, value: ProcessIntakeEviction): Observable<any> {
    return this.http.put(`${this.resourceUrl}/process/${id}`, value, {responseType: 'text'});
  }

  loadIntakeEviction(id: string): Observable<IntakeEviction> {
    return this.http.get<IntakeEviction>(`${this.resourceUrl}/${id}`);
  }

  /** Assigns an inbox case to a firm member, or clears it with null; saved immediately (EE-479). */
  assignAttorney(id: string, userId: string | null): Observable<void> {
    return this.http.put<void>(`${this.resourceUrl}/${id}/assigned-attorney`, { user_id: userId });
  }

  /** @deprecated Use attachFromUpload instead. Retained during rollout window. */
  uploadAttachment(intakeEvictionId: string, formData: FormData): Observable<Attachment> {
    return this.http.post<Attachment>(`${this.resourceUrl}/${intakeEvictionId}/attachments`, formData);
  }

  attachFromUpload(intakeEvictionId: string, upload_id: string, type?: string, description?: string, date_received?: Date): Observable<Attachment> {
    return this.http.post<Attachment>(`${this.resourceUrl}/${intakeEvictionId}/attachments/from-upload`, {
      upload_id,
      type: type ?? 'OTHER',
      description,
      date_received
    });
  }

  /**
   * Removes a document the firm attached to an intake eviction.
   *
   * The service refuses anything the client submitted — their documents are the record of what was filed.
   * The caller should only offer this for attachments whose `uploaded_org_type` is ATTORNEY.
   */
  deleteAttachment(intakeEvictionId: string, attachmentId: string): Observable<void> {
    return this.http.delete<void>(`${this.resourceUrl}/${intakeEvictionId}/attachments/${attachmentId}`);
  }

  /**
   * Creates an intake eviction using the upload-on-select flow.
   * Sends JSON with upload_ids instead of multipart FormData with file bytes.
   */
  saveIntakeEvictionWithUploads(type: IntakeEvictionType, intakeEviction: any): Observable<string | undefined> {
    if (type === IntakeEvictionType.INTAKE_FORM) {
      return this.http.post(this.resourceUrl + '/intake-form/with-uploads', intakeEviction, {responseType: 'text'});
    }

    // The response is the new case id when an attorney files. When a client files there is no case yet, and the
    // service returns the new intake eviction's id instead because we ask for it, so the portal can open it in
    // Pending Review. A service that predates the parameter ignores it and answers empty, as it always did.
    return this.http.post(this.resourceUrl + '/with-uploads', intakeEviction, {
      responseType: 'text',
      params: {return_intake_id: 'true'}
    });
  }

  updateIntakeEvictionDetails(intakeEviction: IntakeEviction): Observable<IntakeEviction> {
    return this.http.put<IntakeEviction>(this.resourceUrl, intakeEviction);
  }

  updateTags(id: string, tags: Tag[]): Observable<Tag[]> {
    return this.http.put<Tag[]>(`${this.resourceUrl}/${id}/tags`, tags);
  }

  rejectIntakeEviction(id: string, emailDetails: EmailDetails) {
    return this.http.put(`${this.resourceUrl}/process/${id}/reject`, emailDetails);
  }

  deleteIntakeEviction(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.resourceUrl}/${id}`);
  }

  initScraCheck(intakeEvictionId: string, tenantId: string): Observable<any> {
    return this.http.put(`${this.resourceUrl}/scra-check/${intakeEvictionId}/tenant/${tenantId}`, undefined);
  }

  // Bulk deletion methods

  /**
   * Validate bulk deletion request and return impact summary with timeout and retry
   */
  validateBulkDeletion(request: BulkDeletionValidationRequest): Observable<BulkDeletionValidationResponse> {
    return this.http.post<BulkDeletionValidationResponse>(`${this.resourceUrl}/bulk-delete/validate`, request)
      .pipe(
        timeout(30000), // 30 second timeout
        retry(1), // Retry once on failure
        catchError(this.handleError)
      );
  }

  /**
   * Execute bulk deletion of validated cases with timeout and enhanced error handling
   */
  executeBulkDeletion(request: BulkDeletionRequest): Observable<BulkDeletionResponse> {
    // Calculate timeout based on number of cases (minimum 30 seconds, 2 seconds per case)
    const timeoutMs = Math.max(30000, request.caseIds.length * 2000);
    
    return this.http.post<BulkDeletionResponse>(`${this.resourceUrl}/bulk-delete/execute`, request)
      .pipe(
        timeout(timeoutMs),
        catchError(this.handleError)
      );
  }

  /**
   * Execute bulk processing of submitted inbox cases (promote many intake evictions to active cases).
   * Processing is heavier than deletion (S3 + workflow binding + billables), so the timeout scales more
   * generously per case.
   */
  executeBulkProcess(request: BulkProcessRequest): Observable<BulkProcessResponse> {
    const timeoutMs = Math.max(60000, request.caseIds.length * 4000);

    return this.http.post<BulkProcessResponse>(`${this.resourceUrl}/bulk-process/execute`, request)
      .pipe(
        timeout(timeoutMs),
        catchError(this.handleError)
      );
  }

  /**
   * Same as executeBulkProcess, but streams "progress"/"complete" server-sent events over the same
   * request instead of returning a single response at the end — lets the UI show live progress. Mirrors
   * EvictionService.bulkUpdateFieldsStream.
   */
  executeBulkProcessStream(request: BulkProcessRequest): Observable<SseStreamEvent> {
    return createSseStream(`${this.resourceUrl}/bulk-process/execute-stream`, request, this.authService);
  }

  /**
   * Execute bulk rejection of submitted inbox cases (close each and email its filer the shared message).
   */
  executeBulkReject(request: BulkRejectRequest): Observable<BulkRejectResponse> {
    const timeoutMs = Math.max(30000, request.caseIds.length * 2000);

    return this.http.post<BulkRejectResponse>(`${this.resourceUrl}/bulk-reject/execute`, request)
      .pipe(
        timeout(timeoutMs),
        catchError(this.handleError)
      );
  }

  /** Same as executeBulkReject, but streams progress like executeBulkProcessStream. */
  executeBulkRejectStream(request: BulkRejectRequest): Observable<SseStreamEvent> {
    return createSseStream(`${this.resourceUrl}/bulk-reject/execute-stream`, request, this.authService);
  }

  /**
   * Get bulk deletion audit trail for organization
   */
  getBulkDeletionAuditTrail(organizationId: string): Observable<any> {
    return this.http.get(`${this.resourceUrl}/bulk-delete/audit/${organizationId}`)
      .pipe(
        timeout(15000), // 15 second timeout
        catchError(this.handleError)
      );
  }

  /**
   * Enhanced error handling for bulk deletion operations
   */
  private handleError = (error: any): Observable<never> => {
    // AuthInterceptor's "session lookup never answered" failure is also status 0, but it is not a
    // network problem. Rethrow it untouched: wrapping it in a plain object would hide it from the
    // ErrorHandler's ISS-182 check and report it to RUM as "Network connection failed".
    if (error instanceof HttpErrorResponse && error.status === 0 && error.statusText === AUTH_SESSION_TIMEOUT_STATUS_TEXT) {
      return throwError(() => error);
    }

    let errorMessage = 'An unexpected error occurred';
    
    if (error.name === 'TimeoutError') {
      errorMessage = 'Request timed out. The operation may still be processing.';
    } else if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        errorMessage = 'Network connection failed. Please check your internet connection.';
      } else if (error.status >= 400 && error.status < 500) {
        errorMessage = error.error?.message || `Client error (${error.status})`;
      } else if (error.status >= 500) {
        errorMessage = error.error?.message || 'Server error occurred. Please try again later.';
      }
    }

    // Preserve the original error structure while adding enhanced message
    const enhancedError = {
      ...error,
      message: errorMessage,
      originalError: error
    };

    return throwError(() => enhancedError);
  };
}
